/**
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useCallback, useEffect } from 'react';
import { WarningRounded } from '@mui/icons-material';
import { Box, IconButton, Table, TableBody, TableHead, TableRow } from '@mui/material';
import { Feature } from '@turf/helpers';
import isNil from 'lodash/isNil';
import size from 'lodash/size';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { withTheme } from 'styled-components';
import FlightLeg from '../../../../../../shared/entities/flightLeg';
import {
  selectFlightLegServiceAlerts,
  selectFlightLegsOrderedByLegOrder,
  selectIsRequestingFlightLegs,
  selectMissionOverlayFlightLegsSortDirection,
  selectMissionOverlayFlightLegsSortField,
  selectRequestingFlightLegsError,
  selectSelectedOverlaidFlightLegId,
  selectselectedOverlaidMissionPlanId,
  selectTerminalDetailsDatapathUuid,
  selectUserId,
} from '../../../../../../store';
import { getFlightLegs } from '../../../../../../store/flightLegs/actions';
import { getFlightLegServiceAlerts } from '../../../../../../store/flightLegServiceAlerts/actions';
import {
  hideMissionOverlayFlightLegListInModal,
  hideMissionOverlayMissionListModal,
  setMissionOverlayFlightLegsSortDirection,
  setMissionOverlayFlightLegsSortField,
  showMissionOverlayMissionListInModal,
  showMissionOverlayMissionListModal,
} from '../../../../../../store/ui/actions';
import { clearMissionData, setSelectedOverlaidFlightLegId } from '../../../../../../store/flightLegs/actions';
import {
  removeBeamFromMap,
  clearMission,
  showOverlaidFlightLeg,
  setFlightPathBeamsChecked,
  FTV_MAP_ID,
} from '../../../../../../store/maps/actions';
import { minutesToFormattedMinutesString } from '../../../../../../utils/DateTimeUtils';
import Scrollbars from '../../../../../common/layout/Scrollbars';
import appTheme from '../../../../../common/theme/appTheme.json';
import StyledProps from '../../../../../common/theme/AppThemeInterface';
import {
  ErrorRequestingMissionsMessageContainer,
  StyledArrowDownwardRounded,
  StyledArrowForwardRounded,
  StyledCheckRounded,
  StyledCircularProgress,
  StyledCloseRounded,
  StyledOverlayMissionText,
  StyledPanelWrapper,
  StyledTableCell,
  StyledTableHeaderCell,
  StyledTableRow,
  StyledTableSortLabel,
  StyledTryAgainButton,
} from '../CommonStyledComponents';

export interface MissionPlanListContainerProps extends StyledProps {}

const FlightLegListContainer = (props: MissionPlanListContainerProps) => {
  const dispatch = useDispatch();

  const selectedOverlaidMissionPlanId = useSelector(selectselectedOverlaidMissionPlanId(FTV_MAP_ID), shallowEqual);
  const selectedOverlaidFlightLegId = useSelector(selectSelectedOverlaidFlightLegId(FTV_MAP_ID), shallowEqual);
  const datapathUuid = useSelector(selectTerminalDetailsDatapathUuid, shallowEqual);
  const userId = useSelector(selectUserId, shallowEqual);
  const flightLegs = useSelector(selectFlightLegsOrderedByLegOrder, shallowEqual);
  const flightLegServiceAlerts = useSelector(selectFlightLegServiceAlerts, shallowEqual);
  const requestingFlightLegsError = useSelector(selectRequestingFlightLegsError, shallowEqual);
  const isRequestingFlightLegs = useSelector(selectIsRequestingFlightLegs, shallowEqual);
  const missionOverlayFlightLegsSortField = useSelector(selectMissionOverlayFlightLegsSortField, shallowEqual);
  const missionOverlayFlightLegsSortDirection = useSelector(selectMissionOverlayFlightLegsSortDirection, shallowEqual);

  const fetchFlightLegs = useCallback(() => {
    if (!isNil(selectedOverlaidMissionPlanId) && !isNil(datapathUuid) && !isNil(userId)) {
      dispatch(getFlightLegs(selectedOverlaidMissionPlanId, datapathUuid, userId));
    }
  }, [dispatch, selectedOverlaidMissionPlanId, datapathUuid, userId]);

  useEffect(() => {
    fetchFlightLegs();
  }, [fetchFlightLegs]);

  const getSortArrow = (columnTitle: React.ReactElement, columnSortField: keyof FlightLeg) => (
    <StyledTableSortLabel
      active={columnSortField === missionOverlayFlightLegsSortField}
      onClick={() => {
        if (columnSortField === missionOverlayFlightLegsSortField) {
          dispatch(
            setMissionOverlayFlightLegsSortDirection(missionOverlayFlightLegsSortDirection === 'asc' ? 'desc' : 'asc'),
          );
        } else {
          dispatch(setMissionOverlayFlightLegsSortField(columnSortField));
        }
      }}
      direction={missionOverlayFlightLegsSortDirection}
      IconComponent={StyledArrowDownwardRounded}
    >
      {columnTitle}
    </StyledTableSortLabel>
  );
  const handleFlightLegListRowClicked = useCallback(
    (flightLeg: FlightLeg) => {
      const { id } = flightLeg;
      if (!isNil(selectedOverlaidMissionPlanId) && !isNil(userId) && !isNil(id)) {
        if (
          selectedOverlaidFlightLegId &&
          id !== selectedOverlaidFlightLegId &&
          flightLegServiceAlerts &&
          flightLegServiceAlerts[selectedOverlaidFlightLegId] &&
          flightLegServiceAlerts[selectedOverlaidFlightLegId].beams &&
          size(flightLegServiceAlerts[selectedOverlaidFlightLegId].beams.features)
        ) {
          flightLegServiceAlerts[selectedOverlaidFlightLegId].beams.features.forEach((feature: Feature) => {
            dispatch(removeBeamFromMap(FTV_MAP_ID)(
              feature.properties?.name,
              feature.properties?.network, 
              feature.properties?.mapType
            ));
          });
        }

        dispatch(setSelectedOverlaidFlightLegId(FTV_MAP_ID)(id));

        dispatch(getFlightLegServiceAlerts(selectedOverlaidMissionPlanId, id, userId));
        dispatch(hideMissionOverlayMissionListModal());

        dispatch(showOverlaidFlightLeg(FTV_MAP_ID)());

        dispatch(setFlightPathBeamsChecked(FTV_MAP_ID)(true));
      }
    },
    [dispatch, selectedOverlaidMissionPlanId, userId, selectedOverlaidFlightLegId, flightLegServiceAlerts],
  );

  const handleMissionPlansModalCloseClicked = () => {
    // If a flight leg was not selected, remove the selected mission id
    if (isNil(selectedOverlaidFlightLegId)) {
      dispatch(hideMissionOverlayMissionListModal());
      dispatch(clearMission(FTV_MAP_ID)());
      dispatch(clearMissionData(FTV_MAP_ID)());
    }
    dispatch(hideMissionOverlayMissionListModal());
  };

  const handleOverlayMissionBreadcrumbClicked = () => {
    // If a flight leg was not selected, remove the selected mission id
    if (isNil(selectedOverlaidFlightLegId)) {
      dispatch(hideMissionOverlayMissionListModal());
      dispatch(clearMission(FTV_MAP_ID)());
      dispatch(clearMissionData(FTV_MAP_ID)());
    }
    dispatch(hideMissionOverlayFlightLegListInModal());
    dispatch(showMissionOverlayMissionListInModal());
    dispatch(showMissionOverlayMissionListModal());
  };

  return (
    <StyledPanelWrapper>
      <Box
        display="flex"
        bgcolor={appTheme.primaryBackgroundColor}
        justifyContent="space-between"
        alignItems="center"
        pt="4px"
        pb="4px"
      >
        <Box color={`${appTheme.primaryTextColor}e1`} paddingLeft="16px" fontSize="15px" letterSpacing="0.14px">
          <StyledOverlayMissionText id="overlayMissionBreadcrumb" onClick={handleOverlayMissionBreadcrumbClicked}>
            Overlay Mission{' '}
          </StyledOverlayMissionText>
          / Overlay Flight Legs
        </Box>
        <IconButton data-test="close_flight_leg_list_button" onClick={handleMissionPlansModalCloseClicked}>
          <StyledCloseRounded />
        </IconButton>
      </Box>
      <Box>
        {isRequestingFlightLegs && !size(flightLegs) ? (
          <Box height="342px" paddingTop="12px" width="100%" display="flex" justifyContent="center" alignItems="center">
            <StyledCircularProgress className="progress" />
          </Box>
        ) : size(flightLegs) ? (
          <Scrollbars autoHeight autoHeightMin={0} autoHeightMax="375px">
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <StyledTableHeaderCell>
                    <Box ml="16px">{getSortArrow(<>Leg</>, 'legOrder')}</Box>
                  </StyledTableHeaderCell>
                  <StyledTableHeaderCell>{getSortArrow(<>Origin</>, 'origin')}</StyledTableHeaderCell>
                  <StyledTableHeaderCell>{getSortArrow(<>Destination</>, 'destination')}</StyledTableHeaderCell>
                  <StyledTableHeaderCell>{getSortArrow(<>Waypoints</>, 'numberOfWaypoints')}</StyledTableHeaderCell>

                  <StyledTableHeaderCell>{getSortArrow(<>Flight Time</>, 'durationMin')}</StyledTableHeaderCell>

                  <StyledTableHeaderCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {flightLegs
                  .slice()
                  .sort((a: FlightLeg, b: FlightLeg) => {
                    if (missionOverlayFlightLegsSortDirection === 'asc') {
                      return a[missionOverlayFlightLegsSortField] > b[missionOverlayFlightLegsSortField] ? 1 : -1;
                    } else {
                      return a[missionOverlayFlightLegsSortField] < b[missionOverlayFlightLegsSortField] ? 1 : -1;
                    }
                  })
                  .map((flightLeg: FlightLeg) => (
                    <StyledTableRow
                      key={flightLeg.id}
                      data-test="flight_legs_overlay_list_table_row"
                      onClick={() => {
                        handleFlightLegListRowClicked(flightLeg);
                      }}
                    >
                      <StyledTableCell>
                        <Box marginLeft="16px" width="235px">
                          Leg {flightLeg.legOrder}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>{flightLeg.origin}</StyledTableCell>
                      <StyledTableCell>{flightLeg.destination}</StyledTableCell>
                      <StyledTableCell>{flightLeg.numberOfWaypoints}</StyledTableCell>
                      <StyledTableCell>{minutesToFormattedMinutesString(flightLeg.durationMin)}</StyledTableCell>

                      <StyledTableCell>
                        <IconButton data-test="row_forward_arrow_action" size="small">
                          {selectedOverlaidFlightLegId === flightLeg.id ? (
                            <StyledCheckRounded />
                          ) : (
                            <StyledArrowForwardRounded />
                          )}
                        </IconButton>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
              </TableBody>
            </Table>
          </Scrollbars>
        ) : (
          <Box
            color={`${appTheme.primaryTextColor}cc`}
            textAlign="center"
            width="100%"
            pt="52px"
            letterSpacing="-0.14px"
            fontSize="12px"
          >
            (No Flight Legs for this Mission)
          </Box>
        )}
      </Box>

      {!isNil(requestingFlightLegsError) && (
        <div className="loading__error__container">
          <div className="loading__error__message__container">
            <WarningRounded />
            <ErrorRequestingMissionsMessageContainer>
              Error Retrieving Flight Legs Plans
            </ErrorRequestingMissionsMessageContainer>
            <StyledTryAgainButton variant="text" onClick={fetchFlightLegs}>
              Retry
            </StyledTryAgainButton>
          </div>
        </div>
      )}
    </StyledPanelWrapper>
  );
};

export default withTheme(FlightLegListContainer);
